import React, { useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tab,
  Tabs,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import AdminLayout from '../../layouts/AdminLayout';
import { AdminService } from '../../services/adminService';
import ErrorClassificationCachePanel from './ErrorClassificationCachePanel';
import ErrorClassificationHeuristicPanel from './ErrorClassificationHeuristicPanel';
import {
  getAllCompetences,
  getCompetenceName,
  getNotionLabel,
} from '../../config/shared_config';

const PRESETS = [
  {
    id: 'belle',
    label: 'belle → belles (liste)',
    kind: 'word_lists',
    expected: 'belle',
    produced: 'belles',
    target: { domain: 'orthographe', competence: 'homophones', notion: 'a_a' },
  },
  {
    id: 'et',
    label: 'et → est (liste)',
    kind: 'word_lists',
    expected: 'et',
    produced: 'est',
    target: { domain: 'orthographe', competence: 'homophones', notion: 'ai_et_est' },
  },
  {
    id: 'dict-belles',
    label: 'dictée « belles »',
    kind: 'dictation',
    expected: 'Le soleil brille et la maison est belle',
    produced: 'Le soleil brille et la maison est belles',
    target: { domain: 'orthographe', competence: 'homophones', notion: 'ai_et_est' },
  },
  {
    id: 'dict-maisons',
    label: 'dictée « maisons sont belles »',
    kind: 'dictation',
    expected: 'Les maisons sont belles.',
    produced: 'Les maison son belle.',
    target: { domain: 'orthographe', competence: 'homophones', notion: 'son_sont' },
  },
  {
    id: 'a-a',
    label: 'a → à (liste)',
    kind: 'word_lists',
    expected: 'a',
    produced: 'à',
    target: { domain: 'orthographe', competence: 'homophones', notion: 'a_a' },
  },
];

const SOURCE_COLOR = {
  heuristic: 'success',
  cache: 'info',
  llm: 'warning',
  skipped: 'default',
};

function notionOptions() {
  return getAllCompetences().map((row) => ({
    domain: row.domain,
    competence: row.competence,
    notion: row.notion,
    label: `${row.domain} / ${getCompetenceName(row.domain, row.competence)} / ${getNotionLabel(row.notion)} (${row.notion})`,
  }));
}

export default function ErrorClassificationHarnessScreen() {
  const options = notionOptions();
  const [kind, setKind] = useState('word_lists');
  const [expected, setExpected] = useState('belle');
  const [produced, setProduced] = useState('belles');
  const [target, setTarget] = useState(options.find((o) => o.notion === 'a_a') || null);
  const [skipLlm, setSkipLlm] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [tab, setTab] = useState('harness');

  const applyPreset = (preset) => {
    setKind(preset.kind);
    setExpected(preset.expected);
    setProduced(preset.produced);
    setTarget(options.find((o) => o.notion === preset.target.notion) || {
      ...preset.target,
      label: preset.target.notion,
    });
  };

  const submit = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await AdminService.probeErrorClassification({
        kind,
        expected,
        produced,
        skipLlm,
        target: target
          ? { domain: target.domain, competence: target.competence, notion: target.notion }
          : null,
      });
      setResult(data);
    } catch (e) {
      setError(e?.message || 'Échec du harnais');
      setResult(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AdminLayout>
      <Box sx={{ p: 3, maxWidth: tab === 'harness' ? 1100 : 1400 }}>
        <Typography variant="h4" gutterBottom>Classification des fautes</Typography>
        <Tabs
          value={tab}
          onChange={(_e, v) => setTab(v)}
          sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab value="harness" label="Harnais" />
          <Tab value="cache" label="Cache" />
          <Tab value="heuristic" label="Heuristique" />
        </Tabs>

        {tab === 'cache' && <ErrorClassificationCachePanel />}
        {tab === 'heuristic' && <ErrorClassificationHeuristicPanel />}
        {tab === 'harness' && (
        <>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 820 }}>
          Envoie une dictée ou une liste de mots dans le même code que les routes élève
          (`/dictation/correct`, `/games/wordlists/correct`). Le pipeline (heuristique → cache)
          s&apos;affiche même sans LLM. Cocher « appeler le LLM » écrit le cache comme un vrai combat.
        </Typography>

        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
          {PRESETS.map((p) => (
            <Chip key={p.id} label={p.label} onClick={() => applyPreset(p)} variant="outlined" />
          ))}
        </Stack>

        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Stack spacing={2}>
              <ToggleButtonGroup
                exclusive
                size="small"
                value={kind}
                onChange={(_e, v) => { if (v) setKind(v); }}
              >
                <ToggleButton value="word_lists">Liste de mots</ToggleButton>
                <ToggleButton value="dictation">Dictée (phrase)</ToggleButton>
              </ToggleButtonGroup>

              <TextField
                label={kind === 'word_lists' ? 'Mots attendus (un par ligne)' : 'Texte attendu'}
                value={expected}
                onChange={(e) => setExpected(e.target.value)}
                multiline
                minRows={kind === 'dictation' ? 2 : 1}
              />
              <TextField
                label={kind === 'word_lists' ? 'Mots produits (un par ligne)' : 'Texte élève'}
                value={produced}
                onChange={(e) => setProduced(e.target.value)}
                multiline
                minRows={kind === 'dictation' ? 2 : 1}
              />
              <Autocomplete
                options={options}
                value={target}
                onChange={(_e, v) => setTarget(v)}
                getOptionLabel={(o) => o.label || o.notion || ''}
                isOptionEqualToValue={(a, b) => a.notion === b.notion}
                renderInput={(params) => (
                  <TextField {...params} label="Notion visée (prior, pas une contrainte)" />
                )}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!skipLlm}
                    onChange={(e) => setSkipLlm(!e.target.checked)}
                  />
                }
                label="Appeler le LLM (sinon heuristique + cache seulement)"
              />
              <Box>
                <Button variant="contained" onClick={submit} disabled={loading}>
                  {loading ? 'Correction…' : 'Corriger'}
                </Button>
              </Box>
            </Stack>
          </CardContent>
        </Card>

        {loading && <CircularProgress size={28} sx={{ mb: 2 }} />}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {result && (
          <Stack spacing={2}>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Chip label={result.route} />
              <Chip
                label={`source=${result.source}`}
                color={SOURCE_COLOR[result.source] || 'default'}
              />
              <Chip label={`${result.elapsedMs} ms`} variant="outlined" />
              {result.calledLlm && <Chip label="LLM appelé" color="warning" />}
              <Chip label={`taxonomie ${result.taxonomySize}`} variant="outlined" />
            </Stack>

            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Pipeline (avant / sans LLM)</Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Attendu</TableCell>
                      <TableCell>Produit</TableCell>
                      <TableCell>Norme</TableCell>
                      <TableCell>Heuristique</TableCell>
                      <TableCell>Cache</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(result.pipeline || []).map((row, i) => (
                      <TableRow key={`${row.expectedNorm}-${row.producedNorm}-${i}`}>
                        <TableCell>{row.expected}</TableCell>
                        <TableCell>{row.produced}</TableCell>
                        <TableCell>
                          {row.skipped ? 'identique' : `${row.expectedNorm} → ${row.producedNorm}`}
                        </TableCell>
                        <TableCell>
                          {row.heuristic
                            ? `${row.heuristic.observedNotionId || '—'} (${row.heuristic.errorFamily})`
                            : '—'}
                        </TableCell>
                        <TableCell>
                          {row.cache
                            ? `${row.cache.observedNotionId || '—'} · ${row.cache.source} · hits ${row.cache.hitCount ?? 0}`
                            : '—'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>

            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Réponse de la route</Typography>
                {result.response == null ? (
                  <Alert severity="info">
                    LLM non appelé (pipeline seulement). Décoche la case pour voir le JSON réel.
                  </Alert>
                ) : (
                  <Box
                    component="pre"
                    sx={{
                      m: 0,
                      p: 1.5,
                      bgcolor: 'grey.100',
                      borderRadius: 1,
                      overflow: 'auto',
                      fontSize: 13,
                    }}
                  >
                    {JSON.stringify(result.response, null, 2)}
                  </Box>
                )}
              </CardContent>
            </Card>
          </Stack>
        )}
        </>
        )}
      </Box>
    </AdminLayout>
  );
}
